import { Handle, Position } from 'reactflow';
import { MessageCircle, FileText, Link, Phone, Image, Video, File, Mic } from 'lucide-react';
import { stableButtonId } from '@shared/flow-utils';
import { CartaoDoNo, EntradaDoNo } from './node-chrome';
import { SaidaNomeada } from './node-saida';

const ICONE_DE_MIDIA: Record<string, any> = {
  image: Image,
  video: Video,
  audio: Mic,
  file: File,
};

/**
 * Botões "roteáveis" — os que abrem um caminho próprio no fluxo. URL e
 * telefone abrem coisas no celular do contato e não voltam resposta, então
 * não têm saída.
 */
function botoesRoteaveis(data: any): { id: string; text: string }[] {
  if (data.contentSource === 'template') {
    const tpl = Array.isArray(data.templateButtons) ? data.templateButtons : [];
    return tpl
      .filter((b: any) => b.type === 'QUICK_REPLY')
      .map((b: any) => ({ id: stableButtonId(b.text || ''), text: b.text || 'Botão' }));
  }
  return (data.messageContent?.buttons ?? [])
    .filter((b: any) => b.type === 'text' || b.type === 'postback')
    .map((b: any) => ({ id: b.id, text: b.text || 'Botão' }));
}

export default function ContentNode({ data }: any) {
  const ehTemplate = data.contentSource === 'template';
  const mc = data.messageContent ?? {};
  const roteaveis = botoesRoteaveis(data);
  const links = ehTemplate
    ? []
    : (mc.buttons ?? []).filter((b: any) => b.type === 'url' || b.type === 'phone');
  const IconeMidia = ICONE_DE_MIDIA[mc.type];

  return (
    <>
      <EntradaDoNo />
      <CartaoDoNo
        tipo="content"
        dados={data}
        icone={ehTemplate ? FileText : IconeMidia ?? MessageCircle}
        corDoTopo={ehTemplate ? 'bg-blue-500' : 'bg-green-500'}
        corDoIcone={ehTemplate ? 'text-blue-600' : 'text-green-600'}
        titulo={data.label || (ehTemplate ? 'Enviar modelo aprovado' : 'Enviar mensagem')}
        largura={roteaveis.length > 0 || links.length > 0 ? 'w-[250px]' : 'w-[224px]'}
      >
        {/* Miniatura só para imagem — é a única em que ver vale mais que ler. */}
        {!ehTemplate && mc.type === 'image' && mc.url && (
          <div className="mt-2 rounded overflow-hidden border border-border/40">
            <img
              src={mc.url}
              alt=""
              className="w-full h-16 object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}

        {roteaveis.length > 0 && (
          <div className="mt-2 border-t border-border/40 pt-1.5">
            <div className="text-[9px] text-muted-foreground/60 uppercase font-medium tracking-wide mb-0.5">
              Respostas
            </div>
            {roteaveis.map((b) => (
              <SaidaNomeada key={b.id} id={`btn-${b.id}`} rotulo={b.text} cor="verde" />
            ))}
            <SaidaNomeada id="default" rotulo="Qualquer outra coisa" cor="neutro" />
          </div>
        )}

        {links.length > 0 && (
          <div className="mt-1.5 space-y-0.5">
            {links.map((b: any, i: number) => (
              <div
                key={b.id || i}
                className="text-[10px] text-muted-foreground bg-muted/30 rounded px-2 py-0.5 truncate flex items-center gap-1"
              >
                {b.type === 'url' ? (
                  <Link className="w-2.5 h-2.5 shrink-0" />
                ) : (
                  <Phone className="w-2.5 h-2.5 shrink-0" />
                )}
                {b.text}
              </div>
            ))}
          </div>
        )}
      </CartaoDoNo>

      {/* Sem botões roteáveis, a saída é uma só e fica na borda do cartão. */}
      {roteaveis.length === 0 && (
        <Handle
          type="source"
          position={Position.Right}
          id="default"
          className="!w-2.5 !h-2.5 !bg-gray-300 !border-gray-400"
        />
      )}
    </>
  );
}
